import { Button, Drawer, Flex } from '@mantine/core';
import { FormProvider, useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useMemo } from 'react';

import { drawerTransitionProps } from '@dealstack/frontend/shared/mechanism/styleguide/GlobalStyling.tsx';
import {
	useTranslationCommonUi,
	useTranslationDefault,
	useTranslationMipManage,
} from '@dealstack/frontend/shared/mechanism/i18n/i18nHooks.tsx';
import { showToastV2 } from '@dealstack/frontend/shared/mechanism/toast/useToastV2';
import { isStandardBackendError } from '@dealstack/frontend/shared/dataExchange/ApiLayer/apiLayer';
import {
	ExpectedTransactionType,
	InvestmentDetailsAllocation,
} from '@dealstack/shared/lib/generated';

import { useMipSecurities } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useMipSecurities.ts';

import {
	ALLOCATION_FORM_FIELD,
	AllocationFormValues,
	AllocationSubmitValues,
	buildAllocationFormSchema,
	formatSecurityLabel,
} from './allocationForm';
import { useVestingTermsForDrawer } from './useVestingTermsForDrawer';
import { NumberInput, Select } from 'react-hook-form-mantine';
import { Icon } from '@dealstack/frontend/shared/components/fundament/Icon';
import { useMutation } from '@tanstack/react-query';
import { addInvestmentAllocation } from '@dealstack/frontend/shared/dataExchange/services/mip-manage.service.ts';
import { useRequiredParams } from '@dealstack/frontend/shared/utils/useRequiredParams.ts';
import { useInvestmentDetailsAllocations } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsAllocations.ts';
import { useInvestmentDetailsSummary } from '@dealstack/frontend/app/pages/Project/MIP/MIPManage/InvestmentDetails/hooks/useInvestmentDetailsSummary.ts';

interface Props {
	opened: boolean;
	onClose: () => void;
	existingAllocations: InvestmentDetailsAllocation[];
}

const EMPTY_VALUES: AllocationFormValues = {
	securityId: '',
	units: undefined,
	cost: undefined,
	vestingTermId: null,
	expectedTransactionType: null,
};

export const AddInvestmentAllocationDrawer = ({ opened, onClose, existingAllocations }: Props) => {
	const tMipManage = useTranslationMipManage();
	const tCommonUi = useTranslationCommonUi();
	const tDefault = useTranslationDefault();
	const { data: securities = [] } = useMipSecurities();
	const vestingTermOptions = useVestingTermsForDrawer();
	const { mipId, investmentId } = useRequiredParams('mipId', 'investmentId');
	const addAllocationRequest = useMutation({ mutationFn: addInvestmentAllocation });
	const { refetch: refetchAllocations } = useInvestmentDetailsAllocations();
	const { refetch: refetchSummary } = useInvestmentDetailsSummary();

	const schema = useMemo(() => buildAllocationFormSchema(tCommonUi), [tCommonUi]);

	const form = useForm<AllocationFormValues, unknown, AllocationSubmitValues>({
		resolver: zodResolver(schema),
		defaultValues: EMPTY_VALUES,
	});

	useEffect(() => {
		if (opened) {
			form.reset(EMPTY_VALUES);
		}
	}, [opened, form]);

	const securityOptions = useMemo(() => {
		const allocatedIds = new Set(existingAllocations.map((a) => a.securityId));
		return securities
			.filter((s) => !s.deleted)
			.filter((s) => !allocatedIds.has(s.id))
			.map((s) => ({ value: s.id, label: formatSecurityLabel(s) }));
	}, [securities, existingAllocations]);

	const onSubmit = async (values: AllocationSubmitValues) => {
		addAllocationRequest.mutate(
			{
				mipId,
				investmentId,
				payload: {
					securityId: values.securityId,
					units: values.units,
					cost: values.cost,
					vestingTermId: values.vestingTermId,
					expectedTransactionType: values.expectedTransactionType,
				},
			},
			{
				onSuccess: () => {
					void refetchAllocations();
					void refetchSummary();

					showToastV2({
						title: tMipManage('investment_allocations.drawer.add_success'),
						state: 'success',
					});
					onClose();
				},
				onError: (err) => {
					showToastV2({
						title: tMipManage('investment_allocations.drawer.error'),
						message: isStandardBackendError(err)
							? tDefault(`error_codes.${err.error?.code}`)
							: undefined,
						state: 'error',
					});
				},
			}
		);
	};

	return (
		<Drawer
			opened={opened}
			onClose={onClose}
			position="right"
			transitionProps={drawerTransitionProps}
			title={tMipManage('investment_allocations.drawer.add_title')}
		>
			<FormProvider {...form}>
				<form style={{ height: '100%' }} onSubmit={form.handleSubmit(onSubmit)}>
					<Flex direction="column" h="100%" gap="1rem">
						<Select
							name={ALLOCATION_FORM_FIELD.securityId}
							control={form.control}
							label={tMipManage('investment_allocations.drawer.security_label')}
							placeholder={tMipManage(
								'investment_allocations.drawer.security_placeholder'
							)}
							size="md"
							data={securityOptions}
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							searchable
						/>

						<NumberInput
							name={ALLOCATION_FORM_FIELD.units}
							control={form.control}
							label={tMipManage('investment_allocations.drawer.units_label')}
							placeholder={tMipManage(
								'investment_allocations.drawer.units_placeholder'
							)}
							size="md"
							hideControls
							min={0}
						/>

						<NumberInput
							name={ALLOCATION_FORM_FIELD.cost}
							control={form.control}
							label={tMipManage('investment_allocations.drawer.total_cost_label')}
							placeholder={tMipManage(
								'investment_allocations.drawer.total_cost_placeholder'
							)}
							size="md"
							hideControls
							min={0}
							decimalScale={2}
						/>

						<Select
							name={ALLOCATION_FORM_FIELD.expectedTransactionType}
							control={form.control}
							label={tMipManage(
								'investment_allocations.drawer.transaction_type_label'
							)}
							placeholder={tMipManage(
								'investment_allocations.drawer.transaction_type_placeholder'
							)}
							size="md"
							data={Object.values(ExpectedTransactionType).map((value) => ({
								value,
								label: value,
							}))}
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							clearable
						/>

						<Select
							name={ALLOCATION_FORM_FIELD.vestingTermId}
							control={form.control}
							label={tMipManage('investment_allocations.drawer.vesting_term_label')}
							placeholder={tMipManage(
								'investment_allocations.drawer.vesting_term_placeholder'
							)}
							size="md"
							data={vestingTermOptions}
							rightSection={<Icon variant="caret-down" size="lg" />}
							rightSectionProps={{ style: { pointerEvents: 'none' } }}
							clearable
						/>

						<Button
							fullWidth
							mt="auto"
							type="submit"
							loading={addAllocationRequest.isPending}
							fw={600}
						>
							{tMipManage('investment_allocations.drawer.save_cta')}
						</Button>

						<Button
							variant="subtle"
							fullWidth
							loading={addAllocationRequest.isPending}
							c="dark.8"
							fw={600}
							onClick={onClose}
						>
							{tMipManage('investment_allocations.drawer.cancel_cta')}
						</Button>
					</Flex>
				</form>
			</FormProvider>
		</Drawer>
	);
};
